<template>
  <iModal
    :visible="isOpen"
    :open="isOpen"
    :title="modalTitle"
    :primary-action-button-label="primaryButtonLabel"
    :primary-action-button-disabled="modalMode === 'view'"
    :primary-action-button-loading="isLoading"
    :show-primary-action-button="modalMode !== 'view'"
    :max-width="600"
    @close="closeModal"
    @click:primary="handleSubmit"
  >
    <template #body>
      <iColumn gap="large">
        <template v-if="modalMode === 'view'">
          <iColumn width="fill">
            <iRow gap="large" width="fill">
              <iTextInput
                v-model="form.name"
                label="Service Package Name"
                width="fill"
                disabled
              />

              <iTextInput
                v-model="form.price"
                label="Price"
                width="fill"
                type="number"
                disabled
              />
            </iRow>

            <iTextArea
              v-model="form.description"
              label="Description"
              :rows="4"
              width="fill"
              disabled
            />
          </iColumn>
        </template>

        <template v-else>
          <iColumn gap="none">
            <iTextInput
              v-model="form.name"
              focus
              label="Service Package Name"
              required
              placeholder="Enter service package name"
              width="fill"
              @input="clearError('name')"
            />
            <iText v-if="errors.name" variant="error">
              {{ errors.name }}
            </iText>
          </iColumn>

          <iColumn gap="none">
            <iTextArea
              v-model="form.description"
              label="Description"
              required
              placeholder="Enter service package description"
              :rows="4"
              width="fill"
              @input="clearError('description')"
            />
            <iText v-if="errors.description" variant="error">
              {{ errors.description }}
            </iText>
          </iColumn>

          <iColumn gap="none">
            <iTextInput
              v-model="form.price"
              label="Price"
              required
              placeholder="What is the price?"
              width="fill"
              type="number"
              :padding-left-multiplier="2"
              @input="clearError('price')"
              @enter="handleSubmit"
            >
              <template v-if="form.price" #prepend>
                <iText>$</iText>
              </template>
            </iTextInput>
            <iText v-if="errors.price" variant="error">
              {{ errors.price }}
            </iText>
          </iColumn>
        </template>
      </iColumn>
    </template>
  </iModal>
</template>

<script>
import { mapState, mapActions } from "pinia";
import { useOrganizationStore } from "@/stores/organization-store";
import { useExpertServicePackageStore } from "@/stores/expert-service-package-store";

export default {
  name: "ExpertServicePackageModal",
  data() {
    return {
      form: {
        name: "",
        description: "",
        price: null,
      },
      errors: {
        name: "",
        description: "",
        price: null,
      },
    };
  },
  computed: {
    ...mapState(useOrganizationStore, ["getOrgData"]),
    ...mapState(useExpertServicePackageStore, [
      "isLoadingCreateExpertPackage",
      "isLoadingUpdateExpertPackage",
      "expertPackageModalOpen",
      "expertPackageModalMode",
      "currentExpertPackage",
    ]),

    orgId() {
      return this.getOrgData.org_id;
    },

    isOpen() {
      return this.expertPackageModalOpen;
    },

    modalMode() {
      return this.expertPackageModalMode;
    },

    modalTitle() {
      const titles = {
        create: "Create New Service Package",
        edit: "Edit Service Package",
        view: "Service Package Details",
      };
      return titles[this.modalMode] || titles.create;
    },

    primaryButtonLabel() {
      const labels = {
        create: "Create Service Package",
        edit: "Save Changes",
      };
      return labels[this.modalMode] || labels.create;
    },

    isLoading() {
      return this.modalMode === "create"
        ? this.isLoadingCreateExpertPackage
        : this.isLoadingUpdateExpertPackage;
    },
  },
  watch: {
    currentExpertPackage(newVal) {
      if (newVal) {
        this.form = {
          name: newVal.name,
          description: newVal.description,
          price: newVal.price,
        };
      } else {
        this.resetForm();
      }
    },
  },
  methods: {
    ...mapActions(useExpertServicePackageStore, [
      "createExpertPackage",
      "updateExpertPackage",
      "closeExpertPackageModal",
    ]),

    clearError(field) {
      this.errors[field] = "";
    },

    clearErrors() {
      this.errors = {
        name: "",
        description: "",
        price: null,
      };
    },

    validate() {
      if (this.modalMode === "view") {
        return true;
      }

      let isValid = true;

      if (!this.form.name) {
        this.errors.name = "Service package name is required";
        isValid = false;
      }

      if (!this.form.description) {
        this.errors.description = "Description is required";
        isValid = false;
      }

      if (!this.form.price) {
        this.errors.price = "Price is required";
        isValid = false;
      } else if (isNaN(parseFloat(this.form.price))) {
        this.errors.price = "Price must be a number";
        isValid = false;
      } else if (parseFloat(this.form.price) < 0) {
        this.errors.price = "Price cannot be negative";
        isValid = false;
      }

      return isValid;
    },

    handleSubmit() {
      if (!this.validate()) {
        return;
      }

      if (this.modalMode === "create") {
        this.createExpertPackage(
          this.orgId,
          this.form.name,
          this.form.description,
          parseFloat(this.form.price),
          () => {
            this.resetForm();
          },
        );
      } else if (this.modalMode === "edit") {
        this.updateExpertPackage(
          this.currentExpertPackage.expertServicePackageId,
          this.form.name,
          this.form.description,
          parseFloat(this.form.price),
          () => {
            this.resetForm();
          },
        );
      }
    },

    resetForm() {
      this.form = {
        name: "",
        description: "",
        price: null,
      };
      this.clearErrors();
    },

    closeModal() {
      this.closeExpertPackageModal();
    },
  },
};
</script>
